import { useEffect, useState } from "react";
import { isIOS, isMobile } from "react-device-detect";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { CrossIcon, MobileAppIcon, UIButton } from "common/components";
import { zIndex } from "common/constants/zIndex";
import { hardNavigate } from "common/utilities/hardNavigate";
import { readFromLocalStorage, writeToLocalStorage } from "common/utilities/localStorage";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getCanDisplayAppBanner } from "entities/permissions/selectors";

const StyledBannerContainer = styled.div<{ isOpen: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.backgroundPrimary};
  border-bottom: ${({ isOpen, theme }) => (isOpen ? `1px solid ${theme.colors.outline}` : "none")};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.fillPrimary};
  display: flex;
  flex-direction: row;
  height: ${({ isOpen }) => (isOpen ? 4 : 0)}rem;
  justify-content: space-between;
  padding: 0 10px;
  position: fixed;
  transition:
    border-bottom 0.3s,
    height 0.3s;
  width: 100%;
  z-index: ${zIndex.mobileAppBanner};
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledCloseIcon = styled(CrossIcon)`
  cursor: pointer;
  padding: 10px;
  height: 16px;
  width: 16px;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledTitle = styled.p`
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
`;

const StyledCaption = styled.p`
  font-size: 0.9rem;
  margin: 0;
`;

const StyledWEFLogoIcon = styled(MobileAppIcon)`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  margin: 0 15px 0 5px;
`;

const iosHideOnPaths = ["/app", "/monitor", "/topics"];

export const MobileAppBanner = () => {
  const { pathname } = useLocation();
  const canDisplayAppBanner = useSelector(getCanDisplayAppBanner);
  const [isOpen, setIsOpen] = useState<boolean>(isMobile);
  const [showOnPath, setShowOnPath] = useState<boolean>(true);
  const translate = useTranslate();

  const onCloseHandle = () => {
    setIsOpen(false);
    writeToLocalStorage("APP_BANNER_USER_CLOSED", true);
  };

  // Hard navigate to trigger deep linking in both iOS and Android
  const onOpenHandle = () =>
    // Different deep link for iOS and Android - iOS deep link has a different subdomain, which is needed to trigger the universal link to open the app
    // See docs here: https://developer.apple.com/documentation/xcode/allowing-apps-and-websites-to-link-to-your-content#Support-universal-links
    hardNavigate(`${isIOS ? import.meta.env.VITE_IOS_DEEPLINK_URL : import.meta.env.VITE_WEBSITE_URL}/app`);

  useEffect(() => {
    if (isIOS && iosHideOnPaths.some((path) => pathname.startsWith(path))) setShowOnPath(false);
  }, [pathname]);

  const savedValue = readFromLocalStorage("APP_BANNER_USER_CLOSED");

  const isDisplaying = isOpen && !savedValue && showOnPath && canDisplayAppBanner;

  return (
    <StyledBannerContainer isOpen={isDisplaying}>
      {isDisplaying && (
        <>
          <StyledRow>
            <StyledCloseIcon onClick={onCloseHandle} />
            <StyledWEFLogoIcon />
            <div>
              <StyledTitle>Strategic IQ</StyledTitle>
              <StyledCaption>{translate("app.journey_banner.message")}</StyledCaption>
            </div>
          </StyledRow>
          <UIButton onClick={onOpenHandle}>{translate("app.journey_banner.open")}</UIButton>
        </>
      )}
    </StyledBannerContainer>
  );
};
